import React from 'react';
import { Link } from 'react-router-dom';
import { Instagram, MessageCircle } from 'lucide-react';

const LOGO_URL =
  'https://customer-assets.emergentagent.com/job_influencer-hub-70/artifacts/tkwsip7b_kisaerkeklogo.png';
const WHATSAPP_URL = 'https://wa.me/971506361418';

// Brand Application link goes to /about, then scrolls to the BrandContact section
// (id="contact" on BrandContact.js). 100ms delay lets the page mount first.
const scrollToBrandForm = () => {
  setTimeout(() => {
    const el = document.getElementById('contact');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  }, 100);
};

const SocialIcon = ({ href, ariaLabel, tooltip, children, testId }) => (
  <div className="relative group">
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={ariaLabel}
      data-testid={testId}
      className="w-10 h-10 rounded-full bg-gray-800 hover:bg-[#FFDD59]/20 hover:text-[#FFDD59] flex items-center justify-center text-gray-400 transition-all"
    >
      {children}
    </a>
    <span
      className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 bg-gray-900 text-white text-xs font-medium rounded-md whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-200 shadow-lg border border-gray-800"
      role="tooltip"
    >
      {tooltip}
      <span className="absolute top-full left-1/2 -translate-x-1/2 -mt-px border-4 border-transparent border-t-gray-900"></span>
    </span>
  </div>
);

const navLinkCls = 'text-gray-400 hover:text-white text-sm transition-colors';

const Footer = () => {
  const year = new Date().getFullYear();
  return (
    <footer
      className="border-t border-gray-800 bg-black mt-auto"
      data-testid="footer"
    >
      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Three-group row: logo+email | nav | social icons */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 mb-6">
          {/* Left: logo + email */}
          <div className="flex items-center gap-4 justify-center md:justify-start">
            <img src={LOGO_URL} alt="Persona Logo" className="h-8" />
            <a
              href="mailto:info@personagency.com"
              className="text-gray-400 hover:text-[#FFDD59] text-sm transition-colors"
              data-testid="footer-email-link"
            >
              info@personagency.com
            </a>
          </div>

          {/* Center: nav links */}
          <nav className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
            <Link to="/" className={navLinkCls} data-testid="footer-nav-home">
              Home
            </Link>
            <Link to="/about" className={navLinkCls} data-testid="footer-nav-about">
              About Us
            </Link>
            <Link
              to="/about"
              onClick={scrollToBrandForm}
              className={navLinkCls}
              data-testid="footer-nav-brand"
            >
              Brand Application
            </Link>
            <Link
              to="/influencer-registration"
              className={navLinkCls}
              data-testid="footer-nav-influencer"
            >
              Influencer Application
            </Link>
            <Link
              to="/privacy-policy"
              className={navLinkCls}
              data-testid="footer-nav-privacy"
            >
              Privacy Policy
            </Link>
          </nav>

          {/* Right: social icons */}
          <div className="flex items-center gap-3 justify-center md:justify-end">
            <SocialIcon
              href="https://www.instagram.com/personaagency"
              ariaLabel="Instagram"
              tooltip="Follow Us"
              testId="footer-ig-personaagency"
            >
              <Instagram size={18} />
            </SocialIcon>
            <SocialIcon
              href="https://www.instagram.com/personaajans"
              ariaLabel="Instagram"
              tooltip="Follow Us"
              testId="footer-ig-personaajans"
            >
              <Instagram size={18} />
            </SocialIcon>
            <SocialIcon
              href={WHATSAPP_URL}
              ariaLabel="WhatsApp"
              tooltip="Reach Us via WhatsApp"
              testId="footer-whatsapp"
            >
              <MessageCircle size={18} />
            </SocialIcon>
          </div>
        </div>

        {/* Bottom: copyright + powered-by */}
        <div className="border-t border-gray-800 pt-4 flex flex-col md:flex-row md:justify-between items-center gap-2">
          <p className="text-gray-500 text-xs text-center md:text-left">
            © {year} Persona. All rights reserved.
          </p>
          <a
            href="https://personaajans.com"
            target="_blank"
            rel="noopener noreferrer"
            className="text-gray-500 text-xs hover:text-[#FFDD59] transition-colors"
            data-testid="footer-powered-by-link"
          >
            Powered by Persona Ajans →
          </a>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
